import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../lib/api.js";
import type { BillingInterval } from "../../../shared/types.js";
import { BUMP_COMBO_MONTHLY_CENTS, ORDER_BUMP_ANNUAL_MULTIPLIER, orderBumpListCents } from "../../../shared/order_bumps.js";

export interface OrderBumpItem {
  key: string;
  headline: string;
  description: string;
  checkbox_label: string;
  image_url: string | null;
  video_url: string | null;
  list_cents: number;
  /** Struck-through "de" price for a paid add-on. Absent on a cached older response. */
  reference_cents?: number;
  kind: "offer" | "gift";
  /** The add-on the checkout highlights (FEATURED_BUMP_KEY), only when it is for sale. */
  featured?: boolean;
}

/**
 * The "take them all" offer for what is on sale to this buyer: shown when two or
 * more add-ons are for sale and they add up to more than the combo price, and
 * active once every one of them is selected (however the buyer got there). The
 * server applies the same rule to what it charges (`bumpComboApplies`).
 */
export function bumpComboState(
  items: OrderBumpItem[],
  selectedKeys: Set<string>,
  interval: BillingInterval,
): { eligible: boolean; active: boolean; offerKeys: string[]; listCents: number; comboCents: number } {
  const offers = items.filter((item) => item.kind === "offer");
  const listCents = offers.reduce((sum, item) => sum + item.list_cents, 0);
  const comboCents = orderBumpListCents(BUMP_COMBO_MONTHLY_CENTS, interval);
  const eligible = offers.length >= 2 && listCents > comboCents;
  return {
    eligible,
    active: eligible && offers.every((item) => selectedKeys.has(item.key)),
    offerKeys: offers.map((item) => item.key),
    listCents,
    comboCents,
  };
}

/**
 * The struck-through "de" price on a paid add-on's card, or null when there is
 * none: only when the reference is above the price, never on a gift. On
 * instalments it is the monthly reference, set against one instalment of the
 * annual price but compared with the monthly price, so a reference at or below
 * the price is never struck because an instalment is smaller than a month.
 */
export function bumpStruckReferenceCents(item: OrderBumpItem, perInstallment: number | null): number | null {
  if (item.kind === "gift" || !item.reference_cents) return null;
  if (item.reference_cents <= item.list_cents) return null;
  return perInstallment ? Math.round(item.reference_cents / ORDER_BUMP_ANNUAL_MULTIPLIER) : item.reference_cents;
}

/** Placeholder while the add-ons are being fetched. */
export function OrderBumpSectionLoading() {
  const { t } = useTranslation();
  return (
    <div className="space-y-3" aria-busy="true">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-purple-deep/30 border-t-purple-deep" />
        {t("checkout.bumps.loading")}
      </div>
      {[0, 1].map((i) => (
        <div key={i} className="rounded-xl border-2 border-dashed border-ink-2/20 px-3.5 py-3 animate-pulse">
          <div className="h-3.5 w-2/3 rounded bg-ink-2/15" />
          <div className="mt-2 h-3 w-full rounded bg-ink-2/10" />
          <div className="mt-3 flex justify-between">
            <div className="h-3.5 w-1/4 rounded bg-ink-2/15" />
            <div className="h-3.5 w-1/4 rounded bg-ink-2/15" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function OrderBumpSection({
  items,
  interval,
  locale,
  currency,
  selectedKeys,
  onToggle,
  onToggleAll,
  installments,
}: {
  items: OrderBumpItem[];
  interval: BillingInterval;
  locale: string;
  currency: "BRL" | "USD";
  selectedKeys: Set<string>;
  onToggle: (key: string, checked: boolean) => void;
  /** Select (or clear) every add-on on sale at once — the combo card. */
  onToggleAll: (keys: string[], checked: boolean) => void;
  /**
   * Annual on the card's interest-free instalments (12): prices read per instalment
   * ("+ 12x R$ 5,83"), struck against the monthly reference price. Left out on PIX,
   * where the year is charged at once.
   */
  installments?: number;
}) {
  const { t } = useTranslation();
  const [videoKey, setVideoKey] = useState<string | null>(null);
  const money = (cents: number) => formatMoney(cents, locale, currency);
  const periodLabel = interval === "monthly" ? t("checkout.bumps.perMonth") : t("checkout.bumps.perYear");
  const perInstallment = interval === "annual" && installments ? installments : null;
  // "+ 12x R$ 5,83" on the annual card; "+ R$ 7,00/mês" or "+ R$ 70,00/ano" otherwise.
  const priceText = (cents: number) =>
    perInstallment ? `${perInstallment}x ${money(Math.round(cents / perInstallment))}` : money(cents);
  const priceSuffix = perInstallment ? "" : periodLabel;

  if (items.length === 0) return null;

  const gifts = items.filter((item) => item.kind === "gift");
  const offers = items.filter((item) => item.kind === "offer");
  const combo = bumpComboState(items, selectedKeys, interval);
  const videoItem = videoKey ? items.find((i) => i.key === videoKey) : null;

  function renderCard(item: OrderBumpItem) {
    const isGift = item.kind === "gift";
    const featured = !isGift && item.featured === true;
    const checked = isGift || selectedKeys.has(item.key);
    const shownCents = perInstallment ? Math.round(item.list_cents / perInstallment) : item.list_cents;
    const reference = bumpStruckReferenceCents(item, perInstallment);
    const percentOff = reference ? Math.round((1 - shownCents / reference) * 100) : 0;
    const frame = isGift
      ? "border-purple-deep/30 bg-purple-deep/5"
      : checked
        ? "border-purple-deep bg-purple/15 ring-2 ring-purple-deep/25"
        : featured
          ? "border-purple-deep/70 bg-gradient-to-br from-purple/10 via-white/70 to-cyan/15 shadow-[0_8px_24px_-12px_rgba(92,57,200,0.55)]"
          : "border-dashed border-ink-2/30 bg-white/40";
    return (
      <label
        key={item.key}
        className={`relative block rounded-xl border-2 px-3.5 py-3 transition-colors ${featured ? "mt-2 pt-4" : ""} ${isGift ? "" : "cursor-pointer"} ${frame}`}
      >
        {featured && (
          <span className="absolute -top-3 left-3.5 inline-flex items-center gap-1 rounded-full bg-grad-primary px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
            ★ {t("checkout.bumps.featuredBadge")}
          </span>
        )}
        <div className="flex gap-3">
          {item.image_url ? (
            <img src={item.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm leading-snug">{item.headline}</div>
            <p className="text-xs text-ink-2 mt-0.5 leading-snug">{item.description}</p>
          </div>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <span className={`flex items-center gap-2 text-xs font-semibold ${checked ? "text-purple-deep" : ""}`}>
            <input
              type="checkbox"
              className="h-4 w-4 accent-purple-deep"
              checked={checked}
              disabled={isGift}
              aria-label={item.checkbox_label}
              onChange={(e) => onToggle(item.key, e.target.checked)}
            />
            {isGift ? t("checkout.bumps.included") : checked ? t("checkout.bumps.added") : t("checkout.bumps.add")}
            {item.video_url ? (
              <button
                type="button"
                className="ml-1 font-normal underline text-purple-deep"
                onClick={(e) => {
                  // Inside the card's label: open the video without toggling the add-on.
                  e.preventDefault();
                  setVideoKey(item.key);
                }}
              >
                {t("checkout.bumps.watchVideo")}
              </button>
            ) : null}
          </span>
          <span className="flex items-center gap-1.5 text-sm whitespace-nowrap">
            {isGift ? (
              <>
                <span className="line-through text-ink-2 text-xs">
                  {priceText(item.list_cents)}
                  {priceSuffix}
                </span>
                <span className="font-semibold text-success">{t("checkout.bumps.free")}</span>
              </>
            ) : (
              <>
                {reference ? <span className="line-through text-ink-2 text-xs">{money(reference)}</span> : null}
                <span className={featured || checked ? "font-semibold text-purple-deep" : "font-medium"}>
                  + {priceText(item.list_cents)}
                  <span className="text-xs font-normal text-ink-2">{priceSuffix}</span>
                </span>
                {reference ? (
                  <span className="rounded-full bg-success/15 px-1.5 py-0.5 text-[11px] font-semibold text-success">
                    {t("checkout.bumps.discountBadge", { percent: percentOff })}
                  </span>
                ) : null}
              </>
            )}
          </span>
        </div>
      </label>
    );
  }

  return (
    <div className="space-y-5">
      {gifts.length > 0 ? (
        <div className="space-y-2.5">
          <div className="text-sm font-semibold">{t("checkout.bumps.giftsTitle")}</div>
          {gifts.map(renderCard)}
        </div>
      ) : null}

      {offers.length > 0 ? (
        <div className="space-y-2.5">
          <div className="text-sm font-semibold">{t("checkout.bumps.offersTitle")}</div>
          {offers.map(renderCard)}

          {combo.eligible ? (
            <label
              className={`block cursor-pointer rounded-xl border-2 px-3.5 py-3 transition-colors ${
                combo.active
                  ? "border-purple-deep bg-purple/15 ring-2 ring-purple-deep/25"
                  : "border-cyan-ui bg-cyan/10"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-sm leading-snug">
                  {t("checkout.bumps.comboTitle", { count: combo.offerKeys.length })}
                </div>
                {/* A real discount (the combo price against the list prices), so it gets the loudest badge. */}
                <span className="shrink-0 rounded-full bg-success px-2.5 py-1 text-xs font-bold text-white shadow-sm">
                  {t("checkout.bumps.discountBadge", {
                    percent: Math.round((1 - combo.comboCents / combo.listCents) * 100),
                  })}
                </span>
              </div>
              <p className="text-xs text-ink-2 mt-0.5 leading-snug">
                {t("checkout.bumps.comboDescription", { separate: `${priceText(combo.listCents)}${priceSuffix}` })}{" "}
                <span className="font-semibold text-success">
                  {t("checkout.bumps.comboSaving", {
                    saving: `${money(combo.listCents - combo.comboCents)}${periodLabel}`,
                  })}
                </span>
              </p>
              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                <span className={`flex items-center gap-2 text-xs font-semibold ${combo.active ? "text-purple-deep" : ""}`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-purple-deep"
                    checked={combo.active}
                    onChange={(e) => onToggleAll(combo.offerKeys, e.target.checked)}
                  />
                  {combo.active ? t("checkout.bumps.comboAdded") : t("checkout.bumps.comboAdd")}
                </span>
                <span className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                  <span className="line-through text-ink-2 text-xs">{priceText(combo.listCents)}</span>
                  <span className="font-semibold text-purple-deep">
                    {priceText(combo.comboCents)}
                    <span className="text-xs font-normal text-ink-2">{priceSuffix}</span>
                  </span>
                </span>
              </div>
            </label>
          ) : null}
        </div>
      ) : null}

      {videoItem?.video_url ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal
        >
          <div className="bg-white rounded-xl max-w-lg w-full p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-sm">{videoItem.headline}</span>
              <button type="button" className="text-sm underline" onClick={() => setVideoKey(null)}>
                {t("checkout.bumps.close")}
              </button>
            </div>
            <video src={videoItem.video_url} controls playsInline className="w-full rounded-lg" />
          </div>
        </div>
      ) : null}
    </div>
  );
}
